/* eslint-disable max-lines */
import { NavigateFunction } from 'react-router-dom';
import { storagePrefixV2 } from '@/constant';
import Toastify from '@/utils/toastify';

export type RobotExceptionValues = {
  image: string;
  title: string;
  content: string;
  confirmText: string;
  cancelText?: string;
  iconName?: string;
  iconColor?: string;
};

export type DialogAction = {
  nav?: NavigateFunction;
  extraActions?: (params?: { type: MassageRuntime.RobotExceptionEvents }) => Promise<void>;
  onOk?: VoidFunc;
  onError?: VoidFunc;
  onFinally?: VoidFunc;
};

type RobotExceptionEventsType = Record<
  MassageRuntime.RobotExceptionEvents,
  {
    name: MassageRuntime.RobotExceptionEvents;
    level?: 'l0' | 'l1' | 'l2' | 'l3' | 'l4';
    l1l2Params?: RobotExceptionValues;
    l3Params?: Exclude<keyof typeof Toastify.l3, 'instance'>;
    l4Params?: Exclude<keyof typeof Toastify.l4, 'instance'>;
    bodyAbnormalTips?: string;
    confirmAction?: (params: DialogAction | null) => void;
    cancelAction?: (params: DialogAction | null) => void;
  }
>;

type RobotException =
  /** 机器人故障 */
  | 'malfunction'
  /** 机器人网络故障 */
  | 'networkError'
  /** 安全工作空间不足 */
  | 'noWorkSpace'
  /** 无法自恢复的机械臂故障 */
  | 'unRecoverableArmError'
  /** 轨迹计算失败 */
  | 'trackCalFail'
  /** 人体穴位识别失败 */
  | 'accuRecoFail'
  /** 急停被按下 */
  | 'pressCrashStop'
  /** 机器人已暂停 */
  | 'alreadyPaused'
  /** 内源热能硬件异常 */
  | 'rfHardwareEpxcetion'
  /** 订单异常 */
  | 'orderException'
  /** 射频力度50N异常提示 */
  | 'wireTangled'
  /** 冲击波扯线异常 */
  | 'swWireTangled'
  /** 筋膜宝外挂机器端开 */
  | 'hotStoneDisconnect'
  /** 冲击波硬件故障 */
  | 'swNotAvailable'
  /** 人中途离开床面异常 */
  | 'noHumanInMassage';

// 机器人异常弹窗Dialog配置
export const RobotExceptionConfig: Record<RobotException, RobotExceptionValues> = {
  // 机器人故障
  malfunction: {
    image: storagePrefixV2 + '/device-exception-notification/robot-fault.png',
    title: '机器人故障',
    content: '非常抱歉，检测到机器人出故障了，<br />麻烦您手动重启一下它吧。',
    confirmText: '结束订单',
    iconName: 'warning-filled',
    iconColor: '#FF9100',
  },
  networkError: {
    image: storagePrefixV2 + '/device-exception-notification/robot-network-fault.png',
    title: '机器人网络故障',
    content: '非常抱歉，检测到机器人网络已断开，<br />麻烦您手动重启一下它吧。',
    confirmText: '我知道了',
    iconName: 'warning-filled',
    iconColor: '#FF9100',
  },
  unRecoverableArmError: {
    image: storagePrefixV2 + '/device-exception-notification/robot-fault.png',
    title: '机械臂异常',
    content: '非常抱歉，机械臂出现异常，<br />请联系售后人员进行处理。',
    confirmText: '结束订单',
    iconName: 'warning-filled',
    iconColor: '#FF9100',
  },
  noWorkSpace: {
    image: storagePrefixV2 + '/device-exception-notification/insufficient-safe-work-space.png',
    title: '安全工作空间不足',
    content: '很抱歉，有些身体部位机器人无法接触，<br />服务中会跳过这些部位提供服务。',
    confirmText: '继续理疗',
    cancelText: '结束订单',
  },
  trackCalFail: {
    image: storagePrefixV2 + '/device-exception-notification/trajectory-calculation-fail.png',
    title: '轨迹计算失败',
    content: '非常抱歉，机器人计算失败了，无法生成理疗方案，<br />请您安抚好顾客情绪，让我们重新下单。',
    confirmText: '结束订单',
  },
  swNotAvailable: {
    image: storagePrefixV2 + '/device-exception-notification/robot-already-paused.png',
    title: '硬件异常',
    content: '磁震波末端异常，理疗师可在结束订单后，尝试重新下单，<br />并联系工程师排查。',
    confirmText: '我知道了',
  },
  accuRecoFail: {
    image: storagePrefixV2 + '/device-exception-notification/acupoint-recognition-fail.png',
    title: '人体穴位识别失败',
    content: '非常抱歉，机器人未能正确识别人体穴位，<br />请您安抚好顾客情绪，让我们重新下单。',
    confirmText: '结束订单',
  },
  pressCrashStop: {
    image: storagePrefixV2 + '/device-exception-notification/press-crash-stop.png',
    title: '急停被按下',
    content: '机器人急停按钮被按下， 请在确认安全的情况下，<br />手动松开急停按钮，再继续下一步。',
    confirmText: '我知道了',
  },
  alreadyPaused: {
    image: storagePrefixV2 + '/device-exception-notification/robot-already-paused.png',
    title: '机器人已暂停',
    content:
      '设备检测到力传感器数据异常，<br />为确保安全与体验，当前服务已暂停。<br />您可以选择「结束订单」或「继续理疗」，<br />如仍提示异常，请联系售后人员进行处理。',
    confirmText: '继续理疗',
    cancelText: '结束订单',
  },
  noHumanInMassage: {
    image: storagePrefixV2 + '/device-exception-notification/robot-already-paused.png',
    title: '机器人已暂停',
    content:
      '检测到床面无人，请确认顾客在床面中间位置，<br />确认后可选择 「继续理疗」,<br />您也可以选择「结束订单」。',
    confirmText: '继续理疗',
    cancelText: '结束订单',
  },
  rfHardwareEpxcetion: {
    image: storagePrefixV2 + '/device-exception-notification/rf-hardware-exception.png',
    title: '内源热能硬件异常',
    content: '很抱歉，机器人的内源热能理疗头出现了异常，<br />无法开关和调节能量。',
    confirmText: '结束订单',
  },
  orderException: {
    image: storagePrefixV2 + '/device-exception-notification/order-exception.png',
    title: '订单异常',
    content: '当前订单异常，理疗师可在结束订单<br />后重新下单。',
    confirmText: '结束订单',
  },
  wireTangled: {
    image: storagePrefixV2 + '/device-exception-notification/withdrawal-safety-tips.png',
    title: '机器人已暂停',
    content: '请检查机器人是否被线束缠绕，并使用工具固定。<br />确保顾客安全后，您可以选择「结束订单」或「继续理疗」。',
    confirmText: '继续理疗',
    cancelText: '结束订单',
  },
  swWireTangled: {
    image: storagePrefixV2 + '/device-exception-notification/wire-straightened-min.png',
    title: '订单异常',
    content: '请检查机器人是否被线束缠绕，<br />并使用工具固定，请确保线束规范后重新下单。',
    confirmText: '我知道了',
  },
  hotStoneDisconnect: {
    image: storagePrefixV2 + '/device-exception-notification/robot-already-paused.png',
    title: '机器人已暂停',
    content:
      '请再次检测筋膜宝外挂机<br />接口是否正常插入，重新插入后可选择「继续理疗」<br />您也可以选择「结束订单」。',
    confirmText: '继续理疗',
    cancelText: '结束订单',
  },
};

export const RobotExceptionEventsHandler: RobotExceptionEventsType = {
  /** 急停被按下-L0 */
  EMERGENCY_STOP: {
    name: 'EMERGENCY_STOP',
    level: 'l0',
    l1l2Params: RobotExceptionConfig.pressCrashStop,
    async confirmAction({ extraActions }) {
      await extraActions?.({ type: 'EMERGENCY_STOP' });
    },
  },
  /** 机械臂错误-L1 */
  ARM_ERROR: {
    name: 'ARM_ERROR',
    level: 'l1',
    l1l2Params: RobotExceptionConfig.malfunction,
    async confirmAction({ extraActions, onFinally }) {
      await extraActions?.({ type: 'ARM_ERROR' });
      onFinally?.();
    },
  },
  /** 机械臂延时错误-L1 */
  ARM_DELAY: {
    name: 'ARM_DELAY',
    level: 'l1',
    l1l2Params: RobotExceptionConfig.malfunction,
    async confirmAction({ nav, extraActions, onFinally }) {
      await extraActions?.({ type: 'ARM_ERROR' });
      onFinally?.();
      nav('/user/member-detail', { replace: true });
    },
  },
  /** 断网超时（手诊中）-L1 */
  NETWORK_BROKEN: {
    name: 'NETWORK_BROKEN',
    level: 'l1',
    l1l2Params: RobotExceptionConfig.networkError,
    confirmAction({ nav }) {
      nav('/user/member-detail', { replace: true });
    },
  },
  /** 无法自恢复的机械臂故障，需要售后介入 */
  UNRECOVERABLE_ARM_ERROR: {
    name: 'UNRECOVERABLE_ARM_ERROR',
    level: 'l1',
    l1l2Params: RobotExceptionConfig.unRecoverableArmError,
    async confirmAction({ extraActions }) {
      await extraActions?.({ type: 'UNRECOVERABLE_ARM_ERROR' });
    },
  },
  /** 轨迹计算失败-L2 */
  TRACK_CALC_FAILED: {
    name: 'TRACK_CALC_FAILED',
    level: 'l2',
    l1l2Params: RobotExceptionConfig.trackCalFail,
    async confirmAction({ extraActions, onFinally }) {
      await extraActions?.({ type: 'TRACK_CALC_FAILED' });
      onFinally?.();
    },
  },
  /** 人体穴位识别失败-L2 */
  ACUPOINTS_RECOG_FAILED: {
    name: 'ACUPOINTS_RECOG_FAILED',
    level: 'l2',
    l1l2Params: RobotExceptionConfig.accuRecoFail,
    async confirmAction({ extraActions, onFinally }) {
      await extraActions?.({ type: 'ACUPOINTS_RECOG_FAILED' });
      onFinally?.();
    },
  },
  /** 工作空间异常-L2 */
  ARM_WORKSPACE_ERROR: {
    name: 'ARM_WORKSPACE_ERROR',
    level: 'l2',
    l1l2Params: RobotExceptionConfig.noWorkSpace,
    async cancelAction({ nav, extraActions, onFinally }) {
      await extraActions?.({ type: 'ARM_WORKSPACE_ERROR' });
      onFinally?.();
      nav('/user/member-detail', { replace: true });
    },
  },
  /** 内源热能断连-L2 */
  RF_LOSE_CONTROL: {
    name: 'RF_LOSE_CONTROL',
    level: 'l2',
    l1l2Params: RobotExceptionConfig.rfHardwareEpxcetion,
    async confirmAction({ extraActions, onFinally }) {
      await extraActions?.({ type: 'RF_LOSE_CONTROL' });
      onFinally?.();
    },
  },
  /** 触发力控保护-L2 */
  FORCE_CONTROL_PROTECT: {
    name: 'FORCE_CONTROL_PROTECT',
    level: 'l2',
    l1l2Params: RobotExceptionConfig.alreadyPaused,
    async confirmAction({ extraActions }) {
      await extraActions?.({ type: 'FORCE_CONTROL_PROTECT' });
    },
    async cancelAction({ extraActions }) {
      await extraActions?.({ type: 'FORCE_CONTROL_PROTECT' });
    },
  },
  /** 筋膜宝外挂机器断开-L2 */
  HOTSTONE_DISCONNECT: {
    name: 'HOTSTONE_DISCONNECT',
    level: 'l2',
    l1l2Params: RobotExceptionConfig.hotStoneDisconnect,
    async confirmAction({ extraActions }) {
      await extraActions?.({ type: 'HOTSTONE_DISCONNECT' });
    },
    async cancelAction({ extraActions }) {
      await extraActions?.({ type: 'HOTSTONE_DISCONNECT' });
    },
  },
  NO_HUMAN_IN_MASSAGE: {
    name: 'NO_HUMAN_IN_MASSAGE',
    level: 'l2',
    l1l2Params: RobotExceptionConfig.noHumanInMassage,
    async confirmAction({ extraActions }) {
      await extraActions?.({ type: 'NO_HUMAN_IN_MASSAGE' });
    },
    async cancelAction({ extraActions }) {
      await extraActions?.({ type: 'NO_HUMAN_IN_MASSAGE' });
    },
  },
  /** 射频力度50N异常提示 */
  WIRE_STRAIGHTENED: {
    name: 'WIRE_STRAIGHTENED',
    level: 'l2',
    l1l2Params: RobotExceptionConfig.wireTangled,
    async confirmAction({ extraActions }) {
      await extraActions?.({ type: 'WIRE_STRAIGHTENED' });
    },
    async cancelAction({ extraActions }) {
      await extraActions?.({ type: 'WIRE_STRAIGHTENED' });
    },
  },
  SHOCKWAVE_NOT_AVAILABLE: {
    name: 'SHOCKWAVE_NOT_AVAILABLE',
    level: 'l2',
    l1l2Params: RobotExceptionConfig.swNotAvailable,
    async confirmAction({ extraActions }) {
      await extraActions?.({ type: 'SHOCKWAVE_NOT_AVAILABLE' });
    },
  },
  /** 连接中断（手诊中）-L3 */
  LOSE_CONNECTION_L3: {
    name: 'LOSE_CONNECTION_L3',
    level: 'l3',
    l3Params: 'networkException',
  },
  /** 机械臂断连（重连中）-L3 */
  ARM_RECONNECTING: {
    name: 'ARM_RECONNECTING',
    level: 'l3',
    l3Params: 'disconnect',
  },
  /** 未趴正 */
  DETECTING_POSE_ERROR: {
    name: 'DETECTING_POSE_ERROR',
    level: 'l3',
    l3Params: 'incorrectPronePosture',
    bodyAbnormalTips: '检测到顾客姿势未趴正',
  },
  /** 未趴平 */
  DETECTING_NOT_FLAT_LYING: {
    name: 'DETECTING_NOT_FLAT_LYING',
    l3Params: 'notProneFlat',
    level: 'l3',
    bodyAbnormalTips: '检测到顾客姿势未趴平',
  },
  /** 未保持静止 */
  DETECTING_NOT_STAYING: {
    name: 'DETECTING_NOT_STAYING',
    l3Params: 'oftenKeepMoving',
    level: 'l3',
    bodyAbnormalTips: '检测到顾客身体移动幅度过大',
  },
  /** 检测到异物 */
  DETECTING_GOT_DISRUPTOR: {
    l3Params: 'hasObstruction',
    name: 'DETECTING_GOT_DISRUPTOR',
    level: 'l3',
    bodyAbnormalTips: '检测到理疗区域有异物',
  },
  /** 腹部设备被手遮挡 */
  HAND_BLOCKING_MASSAGE_AREA: {
    l3Params: 'handBlockMassageArea',
    name: 'HAND_BLOCKING_MASSAGE_AREA',
    level: 'l3',
    bodyAbnormalTips: '检测到理疗区域被手部遮挡',
  },
  /** 检测到腹部干扰物 **/
  ABDOMEN_DISRUPTORS: {
    name: 'ABDOMEN_DISRUPTORS',
    level: 'l3',
    bodyAbnormalTips: '检测到理疗区域不平整',
  },
  /** 请确保理疗区域衣物平整无褶皱，头发无遮挡，且光照均匀 */
  DEPTH_MAP_ABNORMAL: {
    name: 'DEPTH_MAP_ABNORMAL',
    level: 'l3',
    bodyAbnormalTips: '检测到理疗区域不平整',
    l3Params: 'avoidSunlight',
  },
  /** 机械臂故障尝试恢复中 */
  ARM_ERROR_RECOVERING: {
    name: 'ARM_ERROR_RECOVERING',
    level: 'l3',
    l3Params: 'armErrorRecovering',
  },
  /** 连接中断（按摩中）-L4 */
  LOSE_CONNECTION_L4: {
    name: 'LOSE_CONNECTION_L4',
    level: 'l4',
    l4Params: 'offline',
  },
  /** 磁震波断连-L4 */
  SW_LOSE_CONTROL: {
    name: 'SW_LOSE_CONTROL',
    level: 'l4',
    l4Params: 'shockHardwareException',
  },
  /** 磁震波跳过某动作-L4 */
  SW_SKIP_ACTION: {
    name: 'SW_SKIP_ACTION',
    level: 'l4',
    l4Params: 'partLineSkip',
  },
  /** 内源热能请求超时 */
  RF_REQ_TIMEOUT: {
    name: 'RF_REQ_TIMEOUT',
    level: 'l4',
    l4Params: 'rfAdjustFail',
  },
  /** 手诊时发生遮挡 */
  MARKING_BLOCKED: {
    name: 'MARKING_BLOCKED',
    level: 'l4',
    l4Params: 'cameraBlocked',
  },
  /** 冲击波/按摩时机械臂滑落风险提示 */
  POSSIBLE_SLIPPING_WARNING: {
    name: 'POSSIBLE_SLIPPING_WARNING',
    level: 'l4',
    l4Params: 'swSlippingWarning',
  },
  /** 冲击波/按摩时机械臂滑落风险提示 */
  SW_WIRE_STRAIGHTENED: {
    name: 'SW_WIRE_STRAIGHTENED',
    level: 'l4',
    l4Params: 'swWireStraightened',
  },
  /** 理疗面积过小 */
  ABDOMEN_FALSE_AREA: {
    name: 'ABDOMEN_FALSE_AREA',
  },
  /** 腹部理疗面积过小 */
  LIMITED_ABDOMEN_EXPOSED_AREA: {
    name: 'LIMITED_ABDOMEN_EXPOSED_AREA',
    bodyAbnormalTips: '检测到腹部理疗区域过小',
  },
  /** 肩部理疗面积过小 */
  LIMITED_SHOULDER_EXPOSED_AREA: {
    name: 'LIMITED_SHOULDER_EXPOSED_AREA',
    bodyAbnormalTips: '检测到肩部理疗区域过小',
  },
  /** 腰部理疗面积过小 */
  LIMITED_WAIST_EXPOSED_AREA: {
    name: 'LIMITED_WAIST_EXPOSED_AREA',
    bodyAbnormalTips: '检测到腰部理疗区域过小',
  },
  /* 腰背部理疗面积过小*/
  LIMITED_BACK_EXPOSED_AREA: {
    name: 'LIMITED_BACK_EXPOSED_AREA',
    bodyAbnormalTips: '检测到背部理疗区域过小',
  },
  /** 光线检测异常 */
  LIGHT_ABNORMAL: {
    name: 'LIGHT_ABNORMAL',
    bodyAbnormalTips: '检测到理疗区域光照异常',
  },
};
